<!--
  CheckoutPreviewPane.vue

  Live mock of the shopper-facing checkout form, rendered alongside
  StoreCheckoutInfo's three EditorCards. PR C3 of the Store Editor
  Redesign closing chain (2026-06-16).

  Pure presentational:
    - Reads ``campaign`` (Object) from props.
    - Renders disabled inputs / mock labels so the admin sees the
      effect of each toggle without leaving the editor.
    - No emits, no v-model, no API calls, no save plumbing.

  Sections mirror the three Identity / Shipping / Custom-fields
  EditorCards on the left:

    1. Identity     -- First Name / Last Name / Email / Phone show
                       iff their corresponding ``field_*_enabled``
                       column is true.
    2. Shipping     -- one of three modes based on
                       ``campaign.shipping_mode`` (canonical) OR the
                       three legacy ``field_*_enabled`` flags (back-
                       compat for rows that haven't been re-saved
                       since C2).
    3. Custom fields -- enabled custom text + dropdown fields
                       (predefined entries from Identity are
                       excluded so they don't double-render).
-->
<template>
    <aside
        class="checkout-preview-pane"
        data-testid="checkout-preview-pane"
    >
        <h3
            class="checkout-preview-pane__title"
            data-testid="checkout-preview-title"
        >
            Checkout preview
        </h3>

        <!-- Identity section -->
        <section class="cpp-section" data-testid="preview-identity-section">
            <div class="cpp-section__heading">Your info</div>
            <div
                v-if="campaign && campaign.field_first_name_enabled"
                class="cpp-field"
                data-testid="preview-field-first-name"
            >
                <label>First name</label>
                <input type="text" disabled placeholder="First name" />
            </div>
            <div
                v-if="campaign && campaign.field_last_name_enabled"
                class="cpp-field"
                data-testid="preview-field-last-name"
            >
                <label>Last name</label>
                <input type="text" disabled placeholder="Last name" />
            </div>
            <div
                v-if="campaign && campaign.field_company_email_enabled"
                class="cpp-field"
                data-testid="preview-field-email"
            >
                <label>Email</label>
                <input type="email" disabled placeholder="you@company.com" />
            </div>
            <div
                v-if="campaign && campaign.field_phone_enabled"
                class="cpp-field"
                data-testid="preview-field-phone"
            >
                <label>Phone</label>
                <input type="tel" disabled placeholder="(555) 123-4567" />
            </div>
        </section>

        <!-- Shipping section -->
        <section class="cpp-section" data-testid="preview-shipping-section">
            <div class="cpp-section__heading">Shipping</div>
            <!-- Departments in use and the store does not ship to one fixed
                 department: the shopper must pick their department. -->
            <div
                v-if="campaign && campaign.ask_department"
                class="cpp-field"
                data-testid="preview-field-department"
            >
                <label>Department *</label>
                <select disabled>
                    <option>Select your department...</option>
                </select>
            </div>
            <div v-if="resolvedShipMode === 'shopper_address'">
                <p class="cpp-mode-label">Ship to shopper's address</p>
                <div
                    class="cpp-field"
                    data-testid="preview-field-address"
                >
                    <label>Street address</label>
                    <input
                        type="text"
                        disabled
                        placeholder="123 Main St"
                    />
                </div>
                <div class="cpp-field">
                    <label>City</label>
                    <input type="text" disabled placeholder="City" />
                </div>
                <div class="cpp-field cpp-field--row">
                    <div>
                        <label>State</label>
                        <input type="text" disabled placeholder="ST" />
                    </div>
                    <div>
                        <label>ZIP</label>
                        <input type="text" disabled placeholder="00000" />
                    </div>
                </div>
            </div>
            <!-- Company mode. The store doesn't ask the shopper for an
                 address; orders ship to the picked department's address,
                 else the store's ship-to address, else the company's. The
                 shopper-facing note makes that destination clear. -->
            <div
                v-else-if="resolvedShipMode === 'company'"
                data-testid="preview-shipping-company"
            >
                <p class="cpp-mode-label">
                    Ship to company address
                </p>
                <p class="cpp-note" data-testid="preview-shipping-shopper-note">
                    You don't need to enter an address. Your order ships to
                    your company's address on file (or your department's, if
                    set).
                </p>
            </div>
            <!-- Department mode: every order ships to the one department
                 the store editor chose; the shopper is asked for neither an
                 address nor a department. -->
            <div
                v-else-if="resolvedShipMode === 'department'"
                data-testid="preview-shipping-department"
            >
                <p class="cpp-mode-label">
                    Ship to {{ departmentLabel }}
                </p>
                <p class="cpp-note" data-testid="preview-shipping-department-note">
                    You don't need to enter an address. Your order ships to
                    {{ departmentLabel }}'s address on file.
                </p>
            </div>
        </section>

        <!-- Custom fields section -->
        <section class="cpp-section" data-testid="preview-custom-section">
            <div
                v-if="customTextFields.length || customDropdownFields.length"
                class="cpp-section__heading"
            >
                Additional info
            </div>
            <div
                v-for="(label, idx) in customTextFields"
                :key="`cpp-custom-text-${idx}`"
                class="cpp-field"
                data-testid="preview-custom-row"
            >
                <label>{{ label }}</label>
                <input type="text" disabled :placeholder="label" />
            </div>
            <div
                v-for="(field, idx) in customDropdownFields"
                :key="`cpp-custom-dropdown-${idx}`"
                class="cpp-field"
                data-testid="preview-custom-row"
            >
                <label>{{ field.name }}</label>
                <select disabled>
                    <option>Select...</option>
                    <option
                        v-for="(opt, oIdx) in (field.options || [])"
                        :key="`cpp-opt-${idx}-${oIdx}`"
                    >
                        {{ opt }}
                    </option>
                </select>
            </div>
        </section>
    </aside>
</template>

<script>
/**
 * Predefined checkout fields live in Identity / Shipping; they must
 * NOT double-render in the Custom-fields preview block. The list
 * mirrors ``getPredefinedCheckoutFields`` from the Vuex getter that
 * StoreCheckoutInfo consumes -- duplicated here so the preview pane
 * stays pure-presentational and avoids reaching into the store.
 */
const PREDEFINED_CHECKOUT_FIELDS = new Set([
    "First Name",
    "Last Name",
    "Company Email",
    "Phone",
    "Department",
    "Location",
    "Manager",
    "Shopper Location",
]);

export default {
    name: "CheckoutPreviewPane",
    props: {
        campaign: {
            type: Object,
            default: () => ({}),
        },
    },

    computed: {
        /**
         * Canonical shipping mode is ``campaign.shipping_mode``
         * (one of 'shopper_address' / 'company' / 'department').
         * If absent (legacy rows not yet re-saved under C2), derive
         * from the three legacy boolean flags. Default: shopper.
         */
        resolvedShipMode() {
            const c = this.campaign || {};
            if (c.shipping_mode) return c.shipping_mode;
            // Phase 3: locations mode retired; legacy Location/Manager rows
            // resolve to the company fallback.
            if (c.field_location_enabled || c.field_manager_enabled) {
                return "company";
            }
            return "shopper_address";
        },

        departmentLabel() {
            const name = this.campaign && this.campaign.ship_to_department_name;
            return name ? `the ${name} department` : "the chosen department";
        },

        /**
         * Custom text fields = ``checkout_fields`` minus predefined.
         * Predefined entries already render in Identity / Shipping.
         */
        customTextFields() {
            const fields = (this.campaign && this.campaign.checkout_fields) || [];
            return fields.filter(
                (f) => f && !PREDEFINED_CHECKOUT_FIELDS.has(f),
            );
        },

        customDropdownFields() {
            return (this.campaign && this.campaign.dropdown_fields) || [];
        },
    },
};
</script>

<style scoped>
.checkout-preview-pane {
    background: var(--tf-bg, #fafafa);
    border: 1px solid var(--tf-border, #e5e7eb);
    border-radius: var(--tf-radius-md, 12px);
    padding: var(--tf-sp-4, 16px);
    font-family: "Roboto", sans-serif;
    color: var(--tf-fg-2, #374151);
}

.checkout-preview-pane__title {
    font-family: "Poppins", sans-serif;
    font-size: 14px;
    font-weight: 600;
    color: var(--tf-fg-4, #6b7280);
    text-transform: uppercase;
    letter-spacing: 0.6px;
    margin: 0 0 var(--tf-sp-4, 16px) 0;
}

.cpp-section + .cpp-section {
    margin-top: var(--tf-sp-4, 16px);
    padding-top: var(--tf-sp-4, 16px);
    border-top: 1px dashed var(--tf-border, #e5e7eb);
}

.cpp-section__heading {
    font-family: "Poppins", sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: var(--tf-fg-1, #111827);
    margin-bottom: var(--tf-sp-2, 8px);
}

.cpp-field {
    margin-bottom: var(--tf-sp-3, 12px);
}

.cpp-field label {
    display: block;
    font-size: 12px;
    color: var(--tf-fg-4, #6b7280);
    margin-bottom: 2px;
}

.cpp-field input,
.cpp-field select {
    width: 100%;
    padding: 6px 8px;
    font-size: 13px;
    border: 1px solid var(--tf-border, #e5e7eb);
    border-radius: var(--tf-radius-sm, 6px);
    background: var(--tf-bg);
    color: var(--tf-fg-3, #4b5563);
    cursor: not-allowed;
}

.cpp-field--row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--tf-sp-2, 8px);
}

.cpp-mode-label {
    font-size: 13px;
    font-weight: 500;
    color: var(--tf-fg-2, #374151);
    margin: 0 0 var(--tf-sp-2, 8px) 0;
}

.cpp-note {
    font-size: 12px;
    color: var(--tf-fg-4, #6b7280);
    font-style: italic;
    margin: 0;
}
</style>
